import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip,
} from "reactstrap";
import Select from "react-select";
import Calendar from "react-calendar";
import 'react-calendar/dist/Calendar.css';
import { useFormik } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import TableContainer from "../../../../components/Common/TableContainer";
import { Link } from "react-router-dom";
import TextArea from "antd/es/input/TextArea";
import CustomSelect from "components/Common/custom-select";
import StatusCalendar from "components/Common/StatusCalendar/StatusCalendar";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeTimesheetTaskStatus, fetchTimesheetClient, fetchTimesheetList, fetchTimesheetMode, fetchTimesheetModule, fetchTimesheetMonthDetails, fetchTimesheetNonProjectTaskType, fetchTimesheetProject, fetchTimesheetTask, fetchTimesheetTaskType, saveTimesheetApplyDetails } from "store/actions";
import { formatTimeZoneDate } from "common/dateFormat/formatDate";
import Loader from "common/pageLoader";
import Swal from "sweetalert2";
import { useLocation, useHistory } from "react-router-dom";
import useButtonAccess from "components/Common/useButtonAccess";
import getAccessRights from "components/Common/getAccessRights";

function formatStatusDate(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const timeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string" || !timeStr.includes(":")) return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return (isNaN(hours) ? 0 : hours * 60) + (isNaN(minutes) ? 0 : minutes);
};

const TimeSheetApply = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    modes,
    projects,
    modules,
    tasks,
    taskTypes,
    timesheetList,
    monthDetails,
    clientList,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    modes: state.TimesheetTransactionApplyReducer?.modes ?? [],
    projects: state.TimesheetTransactionApplyReducer?.projects ?? [],
    modules: state.TimesheetTransactionApplyReducer?.modules ?? [],
    tasks: state.TimesheetTransactionApplyReducer?.tasks ?? [],
    taskTypes: state.TimesheetTransactionApplyReducer?.taskTypes ?? [],
    timesheetList: state.TimesheetTransactionApplyReducer?.timesheetList ?? [],
    monthDetails: state.TimesheetTransactionApplyReducer?.monthDetails ?? [],
    clientList: state.TimesheetTransactionApplyReducer?.clientList ?? [],

    loading: state.TimesheetTransactionApplyReducer?.loading,
    error: state.TimesheetTransactionApplyReducer?.error,
    success: state.TimesheetTransactionApplyReducer?.success,
  }));

  const { time_tasktype, time_tasknatures, time_taskstatus } = taskTypes;
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeStartDate, setActiveStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [totalWorked, setTotalWorked] = useState("00:00");
  const [totalApproved, setTotalApproved] = useState("00:00");

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    const create_by = authUserDetails[0].user_id
    dispatch(fetchTimesheetMode(unitId));
    dispatch(fetchTimesheetClient(unitId, create_by))
  }, [dispatch]);

  useEffect(() => {
    if (activeStartDate && authDetails) {
      const formData = new FormData()
      formData.append("date", formatTimeZoneDate(activeStartDate))
      formData.append("create_by", authDetails?.user_id)
      formData.append("unit_id", authDetails?.unit_id)
      dispatch(fetchTimesheetMonthDetails(formData))
    }
  }, [activeStartDate, authDetails])

  useEffect(() => {
    if (selectedDate && authDetails) {
      const formData = new FormData()
      formData.append("date", formatStatusDate(selectedDate))
      formData.append("create_by", authDetails?.user_id)
      formData.append("unit_id", authDetails?.unit_id)
      dispatch(fetchTimesheetList(formData))
    }
  }, [selectedDate, authDetails])

  useEffect(() => {
    const minutesToTime = (totalMinutes) => {
      const hours = Math.floor(totalMinutes / 60).toString().padStart(2, "0");
      const minutes = (totalMinutes % 60).toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    };

    let totalWorkedMinutes = 0;
    let totalApprovedMinutes = 0;

    timesheetList.forEach(entry => {
      totalWorkedMinutes += timeToMinutes(entry.time_hours);
      totalApprovedMinutes += timeToMinutes(entry.approved_hours);
    });

    setTotalWorked(minutesToTime(totalWorkedMinutes));
    setTotalApproved(minutesToTime(totalApprovedMinutes));
  }, [timesheetList]);

  useEffect(() => {
    if (success) {
      // Refetch timesheet month details using activeStartDate
      if (activeStartDate) {
        const monthFormData = new FormData();
        monthFormData.append("date", formatTimeZoneDate(activeStartDate));
        monthFormData.append("create_by", authDetails?.user_id);
        monthFormData.append("unit_id", authDetails?.unit_id);
        dispatch(fetchTimesheetMonthDetails(monthFormData));
      }

      // Refetch timesheet list for selected date
      if (selectedDate) {
        const dayFormData = new FormData();
        dayFormData.append("date", formatStatusDate(selectedDate));
        dayFormData.append("create_by", authDetails?.user_id);
        dayFormData.append("unit_id", authDetails?.unit_id);
        dispatch(fetchTimesheetList(dayFormData));
      }

      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formKey, setFormKey] = useState(0); // 🔑
  const handleResetForm = () => {
    formik.resetForm({ values: initialValues });
    setIsEdit(false);
    setEditId(null);
    setFormKey((prev) => prev + 1); // 🔁 force rerender of Selects
  };

  const selectedDateDisableStatus = monthDetails?.find((item) => item.t_date === formatStatusDate(selectedDate))?.disable_date;

  const initialValues = {
    mode: null,
    project: null,
    client: null,
    module: null,
    task: null,
    taskType: null,
    taskNature: null,
    status: null,
    allocatedHours: "",
    workedHours: "",
    description: "",
    remaininghours:"",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      mode: Yup.object()
        .nullable()
        .required("Mode is required"),

      project: Yup.object()
        .nullable()
        .when("mode", {
          is: (mode) => mode?.value === 1,
          then: (schema) => schema.required("Project is required"),
          otherwise: (schema) => schema.notRequired(),
        }),

      client: Yup.object()
        .nullable()
        .when("mode", {
          is: (mode) => mode?.value === 1,
          then: (schema) => schema.required("Client is required"),
          otherwise: (schema) => schema.notRequired(),
        }),

      module: Yup.object()
        .nullable()
        .when("mode", {
          is: (mode) => mode?.value === 1,
          then: (schema) => schema.required("Module is required"),
          otherwise: (schema) => schema.notRequired(),
        }),

      task: Yup.object()
        .nullable()
        .when("mode", {
          is: (mode) => mode?.value === 1,
          then: (schema) => schema.required("Task is required"),
          otherwise: (schema) => schema.notRequired(),
        }),

      taskType: Yup.object()
        .nullable()
        .required("Task Type is required"),

      taskNature: Yup.object()
        .nullable()
        .required("Task Nature is required"),

      status: Yup.object()
        .nullable()
        .required("Status is required"),

      allocatedHours: Yup.string()
        .when("mode", {
          is: (mode) => mode?.value === 1,
          then: Yup.string()
            .required("Allocated hours is required.")
            .matches(/^([0-9]{1,4}):([0-5][0-9])$/, " "),
          otherwise: Yup.string().notRequired()
        }),

      workedHours: Yup.string()
      .required("Worked hours is required.")
      .matches(/^([0-9]{1,4}):([0-5][0-9])$/, " "),
      // .test(
      //   "worked-less-than-allocated",
      //   "Worked hours cannot exceed allocated hours.",
      //   function (value) {
      //     const { allocatedHours, mode } = this.parent;
      //     // Skip validation if mode is not 1
      //     if (mode?.value !== 1) {
      //       return true;
      //     }

      //     // Check only when both times are valid
      //     if (!value || !allocatedHours) return false;
      //     const isValidTime = (str) => /^([0-9]{1,4}):([0-5][0-9])$/.test(str);

      //     if (!isValidTime(value) || !isValidTime(allocatedHours)) return false;
      //     console.log('timeToMinutes(value) > ',timeToMinutes(value))
      //     return timeToMinutes(value) <= timeToMinutes(allocatedHours);
      //   }
      // ),

      description: Yup.string()
        .required("Description is required")
        .min(1, "Min 1 characters.")
        .max(500, "Max 500 characters."),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("timesheet_id", isEdit ? editId : 0);
      formData.append("projectmode_id", values.mode?.value || 0);
      formData.append("emp_id", authDetails?.empid || 0);
      formData.append("t_Date", formatStatusDate(selectedDate));
      formData.append("client_id", values.client?.value || 0);
      formData.append("project_id", values.project?.value || 0);
      formData.append("module_id", values.module?.value || 0);
      formData.append("task_id", values.task?.value || 0);
      formData.append("tasktype_id", values.taskType?.value || 0);
      formData.append("tasknature_id", values.taskNature?.value || 0);
      formData.append("taskstatus_id", values.status?.value || 0);
      formData.append("unit_id", authDetails?.unit_id || 0);
      formData.append("time_duration", values.workedHours || "");
      formData.append("description", values.description || "");
      formData.append("create_by", authDetails?.user_id || 0);
      
      dispatch(saveTimesheetApplyDetails(formData))
    },

  });

  const handleEdit = (row) => {
     formik.setValues({
      mode: { value: row.p_mode_id, label: row.project_mode },
      project: { value: row.project_id, label: row.project },
      client: { value: row.client_id, label: row.client_name },
      module: { value: row.module_id, label: row.module },
      task: { value: row.task_id, label: row.task },
      taskType: { value: row.task_type_id, label: row.task_type },
      taskNature: { value: row.task_nature_id, label: row.task_nature },
      status: { value: row.task_status_id, label: row.task_status },
      allocatedHours: row.allocated_time_hours || "",
      workedHours: row.time_hours || "",
      description: row.description || "",
    });
    setIsEdit(true);
    setEditId(row.timesheet_id);
  };

  // const columns = [    
  //   {
  //     Header: "Action",
  //     className: "status-col",
  //     applyHeaderClass: true,
  //     Cell: (cellProps) => {
  //       const { b_active, timesheet_id } = cellProps.row.original;
  //       const isActive = b_active === 1;
  //       return (
  //       <div className="d-flex gap-3">
  //         <Link to="#" onClick={() => handleToggleStatus(timesheet_id,isActive)}>
  //           <i
  //             className={`mdi mdi-delete font-size-18`}
  //             style={{ color:"red" }}
  //             id={`status-tooltip-${timesheet_id}`}
  //           />
  //           <UncontrolledTooltip placement="top" target={`status-tooltip-${timesheet_id}`}>
  //             {isActive ? "Active" : "Inactive"}
  //           </UncontrolledTooltip>
  //         </Link>
  //         <Link to="#" className="text-success" onClick={() => handleEdit(cellProps.row.original)}>
  //           <i className="bx bxs-edit font-size-18" id={`edit-tooltip-${timesheet_id}`} />
  //           <UncontrolledTooltip placement="top" target={`edit-tooltip-${timesheet_id}`}>
  //             Edit
  //           </UncontrolledTooltip>
  //         </Link>
  //       </div>
  //       );
  //     },
  //   },
  //   { Header: "S.No", Cell: ({ row }) => timesheetList.length - row.index, className: "serial-no", applyHeaderClass: true, },
  //   { Header: "Mode", accessor: "project_mode" },
  //   { Header: "Client", accessor: "client_name" },
  //   { Header: "Project", accessor: "project" },
  //   { Header: "Module", accessor: "module" },
  //   { Header: "Task", accessor: "task" },
  //   { Header: "Type", accessor: "task_type" },
  //   { Header: "Nature", accessor: "task_nature" },
  //   { Header: "Description", accessor: "description" },
  //   { Header: "Task Status", accessor: "task_status" },
  //   { Header: "Allocated Hours", accessor: "allocated_time_hours" },
  //   { Header: "Worked Hours", accessor: "time_hours" },
  //   { Header: "Approved Hours", accessor: "approved_hours" },
  //   { Header: "Approved Status", accessor: "app_status" },
  // ];

  const columns = [
    {
      Header: "Action",
      className: "status-col",
      applyHeaderClass: true,
      Cell: (cellProps) => {
        const { b_active, timesheet_id } = cellProps.row.original;
       
        const isActive = b_active === 1;
        return (
          <div className="d-flex gap-3">

            {/* DELETE */}
            {canDelete && selectedDateDisableStatus === 0 && cellProps.row.original.app_status !== "Approved" && (
              <Link to="#" onClick={() => handleToggleStatus(timesheet_id, isActive)}>
                <i
                  className="mdi mdi-delete font-size-18"
                  style={{ color: "red" }}
                  id={`status-tooltip-${timesheet_id}`}
                />
                <UncontrolledTooltip
                  placement="top"
                  target={`status-tooltip-${timesheet_id}`}
                >
                  {isActive ? "Active" : "Inactive"}
                </UncontrolledTooltip>
              </Link>
            )}

            {/* EDIT */}
            {canEdit  && selectedDateDisableStatus === 0 && cellProps.row.original.app_status !== "Approved" && (
              <Link
                to="#"
                className="text-success"
                onClick={() => handleEdit(cellProps.row.original)}
              >
                <i
                  className="bx bxs-edit font-size-18"
                  id={`edit-tooltip-${timesheet_id}`}
                />
                <UncontrolledTooltip
                  placement="top"
                  target={`edit-tooltip-${timesheet_id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </Link>
            )}

          </div>
        );
      },
    },

  { Header: "S.No", Cell: ({ row }) => timesheetList.length - row.index, className: "serial-no", applyHeaderClass: true },
  { Header: "Mode", accessor: "project_mode" },
  { Header: "Client", accessor: "client_name" },
  { Header: "Project", accessor: "project" },
  { Header: "Module", accessor: "module" },
  { Header: "Task", accessor: "task" },
  { Header: "Type", accessor: "task_type" },
  { Header: "Nature", accessor: "task_nature" },
  { Header: "Description", accessor: "description" },
  { Header: "Task Status", accessor: "task_status" },
  { Header: "Allocated Hours", accessor: "allocated_time_hours" },
  { Header: "Worked Hours", accessor: "time_hours" },
  { Header: "Approved Hours", accessor: "approved_hours" },
  { Header: "Approved Status", accessor: "app_status" },
];

  const handleToggleStatus = (id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete this.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("timesheet_id", id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeTimesheetTaskStatus(formData));
      }
    });
  }; 

  // const handleChangeMode =(selectedOption)=>{
  //   formik.resetForm();
  //   formik.setFieldValue("mode", selectedOption);
  //   if (selectedOption?.value === 2) {
  //     const fromData = new FormData();
  //     fromData.append("unit_id",authDetails?.unit_id)
  //     dispatch(fetchTimesheetNonProjectTaskType(fromData));
  //   }
  // }
  const handleChangeMode = (selectedOption) => {
    // set mode first
    formik.setFieldValue("mode", selectedOption);

    // clear dependent fields ONLY
    formik.setFieldValue("client", null);
    formik.setFieldValue("project", null);
    formik.setFieldValue("module", null);
    formik.setFieldValue("task", null);
    formik.setFieldValue("taskType", null);
    formik.setFieldValue("taskNature", null);
    formik.setFieldValue("status", null);
    formik.setFieldValue("allocatedHours", "");
    formik.setFieldValue("remaininghours", "");

    // fetch non-project task types
    if (selectedOption?.value === 2 && authDetails?.unit_id) {
      const formData = new FormData();
      formData.append("unit_id", authDetails.unit_id);
      dispatch(fetchTimesheetNonProjectTaskType(formData));
    }
  };

  const handleChangeClient = (selectedOption) => {
    formik.setFieldValue("client", selectedOption);
    formik.setFieldValue("project", null);
    formik.setFieldValue("module", null);
    formik.setFieldValue("task", null);
    formik.setFieldValue("taskType", null);
    formik.setFieldValue("taskNature", null);
    formik.setFieldValue("status", null);
    if (selectedOption?.value) {
      const fromData = new FormData();
      fromData.append("client_id",selectedOption.value)
      fromData.append("unit_id",authDetails?.unit_id)
      fromData.append("emp_id",authDetails?.user_id)
      dispatch(fetchTimesheetProject(fromData));
    }
  }

  const handleChangeProject = (selectedOption) => {
    formik.setFieldValue("project", selectedOption);
    formik.setFieldValue("module", null);
    formik.setFieldValue("task", null);
    formik.setFieldValue("taskType", null);
    formik.setFieldValue("taskNature", null);
    formik.setFieldValue("status", null);
    if (selectedOption?.value) {
      const fromData = new FormData();
      fromData.append("project_id",selectedOption.value)
      fromData.append("unit_id",authDetails?.unit_id)
      fromData.append("create_by",authDetails?.user_id)
      dispatch(fetchTimesheetModule(fromData));
    }
  }

  const handleChangeModule = (selectedOption) => {
    formik.setFieldValue("module", selectedOption);
    formik.setFieldValue("task", null);
    formik.setFieldValue("taskType", null);
    formik.setFieldValue("taskNature", null);
    formik.setFieldValue("status", null);
    if (selectedOption?.value) {
      const fromData = new FormData();
      fromData.append("module_id",selectedOption.value)
      fromData.append("unit_id",authDetails?.unit_id)
       fromData.append("create_by",authDetails?.user_id)
      dispatch(fetchTimesheetTask(fromData));
    }
  }

  const handleChangeTask = (selectedOption) => {
    const selectedItem = tasks.find(task => task.task_id === selectedOption?.value);
    formik.setFieldValue("task", selectedOption);
    formik.setFieldValue("taskType", {value:selectedItem?.tasktype_id,label:selectedItem?.task_type});
    formik.setFieldValue("taskNature", {value:selectedItem?.task_nature_id,label:selectedItem?.time_tasknatures});
    formik.setFieldValue("allocatedHours", selectedItem?.allocated_hours);
    formik.setFieldValue("status", null);
    formik.setFieldValue("remaininghours", selectedItem?.remaining_hours);
    if (selectedOption?.value) {
      const fromData = new FormData();
      fromData.append("task_id",selectedOption.value)
      fromData.append("unit_id",authDetails?.unit_id)
      dispatch(fetchTimesheetTaskType(fromData));
    }
  }

  // const formatTimeWithColonOnBlur = (val) => {
  //   const digits = val.replace(/\D/g, "").slice(0, 4);

  //   if (digits.length === 0) return "";
  //   if (digits.length === 1) return `0${digits}:00`;
  //   if (digits.length === 2) return `${digits}:00`;
  //   if (digits.length === 3) return `${digits.slice(0, 2)}:${digits.slice(2)}0`;
  //   return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  // };
  const formatTimeWithColonOnBlur = (val) => {
    // Remove all non-digit and non-colon characters
    let clean = val.replace(/[^0-9:]/g, "");
    // If already in HH:MM format, just pad
    if (/^\d{1,2}:\d{1,2}$/.test(clean)) {
      let [h, m] = clean.split(":");
      h = h.padStart(2, "0");
      m = m.padStart(2, "0");
      // Clamp minutes to 59
      m = Math.min(Number(m), 59).toString().padStart(2, "0");
      return `${h}:${m}`;
    }
    // Remove colons for free typing
    const digits = clean.replace(/:/g, "").slice(0, 4);
    if (digits.length === 0) return "";
    if (digits.length === 1) return `0${digits}:00`;
    if (digits.length === 2) return `${digits.padStart(2, "0")}:00`;
    if (digits.length === 3) return `${digits.slice(0, 1).padStart(2, "0")}:${digits.slice(1)}`;
    // 4 digits: first 2 = hours, last 2 = minutes
    let h = digits.slice(0, 2);
    let m = digits.slice(2, 4);
    // Clamp minutes to 59
    m = Math.min(Number(m), 59).toString().padStart(2, "0");
    return `${h}:${m}`;
  };
 
 

  return (
    <div className="page-content">
      <Loader loading={loading}/>
      <Container fluid>
        <Breadcrumbs title="Time Sheet" breadcrumbItem="Apply" />
        <Card>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between">
              <Col sm={12} xl={8} className="pe-xl-4 apporder-xl-1">
                <Form onSubmit={formik.handleSubmit}>
                  {/* <Row className="mb-2"> */}
                    <Row>
                      <Col md={6}>
                      <FormGroup className="d-flex align-items-center">
                        <Label className="col-md-4 col-3 pe-3">Mode <span className="text-danger">*</span></Label>
                        <div className="col-md-8 col-9">
                          <CustomSelect
                            key={formKey}
                            options={(modes || []).map((item)=>({value:item.mode_id,label:item.mode_name}))}
                            value={formik.values.mode || null}
                            onChange={selectedOption => {
                              handleChangeMode(selectedOption)
                            }}
                          />
                          {formik.touched.mode && formik.errors.mode && (
                            <div className="text-danger">{formik.errors.mode}</div>
                          )}
                        </div>
                      </FormGroup>
                    </Col>
                    {formik.values.mode?.value === 1 ? (
                    <>
                      <Col md={6}>
                        <FormGroup className="d-flex align-items-center">
                          <Label className="col-md-4 col-3 pe-3">Client <span className="text-danger">*</span></Label>
                          <div className="col-md-8 col-9">
                            <CustomSelect
                              key={formKey}
                              options={(clientList || []).map((item)=>({value:item.client_id,label:item.client_name}))}
                              value={formik.values.client || null}
                              onChange={selectedOption => {
                                handleChangeClient(selectedOption)
                              }}
                            />
                            {formik.touched.client && formik.errors.client && (
                              <div className="text-danger">{formik.errors.client}</div>
                            )}</div>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="d-flex align-items-center">
                          <Label className="col-md-4 col-3 pe-3">Project <span className="text-danger">*</span></Label>
                          <div className="col-md-8 col-9">
                            <CustomSelect
                              key={formKey}
                              options={(projects || []).map((item)=>({value:item.project_id,label:item.project_name}))}
                              value={formik.values.project || null}
                              onChange={selectedOption => {
                                handleChangeProject(selectedOption)
                              }}
                            />
                            {formik.touched.project && formik.errors.project && (
                              <div className="text-danger">{formik.errors.project}</div>
                            )}</div>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="d-flex align-items-center">
                          <Label className="col-md-4 col-3 pe-3">Module <span className="text-danger">*</span></Label>
                          <div className="col-md-8 col-9">
                            <CustomSelect
                              key={formKey}
                              options={(modules || []).map((item)=>({value:item.module_id,label:item.module}))}
                              value={formik.values.module || null}
                              onChange={selectedOption => {
                                handleChangeModule(selectedOption)
                              }}
                            />

                            {formik.touched.module && formik.errors.module && (
                              <div className="text-danger">{formik.errors.module}</div>
                            )}</div>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="d-flex align-items-center">
                          <Label className="col-md-4 col-3 pe-3">Task <span className="text-danger">*</span></Label>
                          <div className="col-md-8 col-9">
                            <CustomSelect
                              key={formKey}
                              options={(tasks || []).map((item)=>({value:item.task_id,label:item.time_task}))}
                              value={formik.values.task || null}
                              onChange={selectedOption => {
                                handleChangeTask(selectedOption)
                              }}
                            />

                            {formik.touched.task && formik.errors.task && (
                              <div className="text-danger">{formik.errors.task}</div>
                            )}</div>
                        </FormGroup>
                      </Col>
                    </>
                    ):null}
                    
                    <Col md={6}>
                      <FormGroup className="d-flex align-items-center">
                        <Label className="col-md-4 col-3 pe-3">Task Type <span className="text-danger">*</span></Label>
                        <div className="col-md-8 col-9">
                          <CustomSelect
                            key={formKey}
                            options={(time_tasktype || []).map((item)=>({value:item.tasktype_id,label:item.task_type}))}
                            value={formik.values.taskType || null}
                            onChange={(selected) => formik.setFieldValue("taskType", selected)}
                            isDisabled={formik.values.mode?.value === 1}
                          />
                          {formik.touched.taskType && formik.errors.taskType && (
                            <div className="text-danger">{formik.errors.taskType}</div>
                          )}</div>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="d-flex align-items-center">
                        <Label className="col-md-4 col-3 pe-3">Task Nature <span className="text-danger">*</span></Label>
                        <div className="col-md-8 col-9">
                          <CustomSelect
                            key={formKey}
                            options={(time_tasknatures || []).map((item)=>({value:item.task_nature_id,label:item.task_nature}))}
                            value={formik.values.taskNature || null}
                            onChange={(selected) => formik.setFieldValue("taskNature", selected)}
                            isDisabled={formik.values.mode?.value === 1}
                          />
                          {formik.touched.taskNature && formik.errors.taskNature && (
                            <div className="text-danger">{formik.errors.taskNature}</div>
                          )}</div>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="d-flex align-items-center">
                        <Label className="col-md-4 col-3 pe-3">Status <span className="text-danger">*</span></Label>
                        <div className="col-md-8 col-9">
                          <CustomSelect
                            key={formKey}
                            options={(time_taskstatus || []).map((item)=>({value:item.status_id,label:item.status_name}))}
                            value={formik.values.status || null}
                            onChange={(selected) => formik.setFieldValue("status", selected)}
                          />
                          {formik.touched.status && formik.errors.status && (
                            <div className="text-danger">{formik.errors.status}</div>
                          )}</div>
                      </FormGroup>
                    </Col>
                    {formik.values.mode?.value === 1 && (
                      <Col md={6}>
                      <FormGroup className="d-flex align-items-center">
                        <Label className="col-md-4 col-3 pe-3">Allocated Hours <span className="text-danger">*</span></Label>
                        <div className="col-md-8 col-9">
                          <Input name="allocatedHours" key={formKey} placeholder="HH:MM" value={formik.values.allocatedHours} onChange={formik.handleChange} disabled />
                        </div>
                      </FormGroup>
                    </Col>
                    )}
                     {formik.values.mode?.value === 1 && (
                      <Col md={6}>
                        <FormGroup className="d-flex align-items-center">
                          <Label className="col-md-4 col-3 pe-3">Remaning Hours </Label>
                          <div className="col-md-8 col-9">
                            <Input name="remaininghours" key={formKey} placeholder="HH:MM" value={formik.values.remaininghours} onChange={formik.handleChange} disabled />
                          </div>
                        </FormGroup>
                      </Col>
                    )}
                    <Col md={6}>
                      <FormGroup className="d-flex align-items-center">
                        <Label className="col-md-4 col-3 pe-3">Worked Hours <span className="text-danger">*</span></Label>
                        <div className="col-md-8 col-9">
                          <Input name="workedHours" key={formKey} placeholder="HH:MM" value={formik.values.workedHours} 
                            onChange={formik.handleChange} // allow free typing
                            onBlur={(e) => {
                              const formatted = formatTimeWithColonOnBlur(e.target.value);
                              formik.setFieldValue("workedHours", formatted);
                              formik.handleBlur(e); // trigger validation
                            }}
                         />
                          {formik.touched.workedHours && formik.errors.workedHours && (
                            <div className="text-danger">{formik.errors.workedHours}</div>
                          )}</div>
                      </FormGroup>
                    </Col>
                      </Row>               
                  <Row className="mb-2"> 
                    <Col >
                      <FormGroup className="d-flex align-items-center">
                        <Label className="appl-lft">Description <span className="text-danger">*</span></Label>
                        <div className="appl-rht">
                          <TextArea name="description" key={formKey} value={formik.values.description} onChange={formik.handleChange} />
                          {formik.touched.description && formik.errors.description && (
                            <div className="text-danger">{formik.errors.description}</div>
                          )}</div>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    {/* <Col className="d-flex justify-content-end">
                      <Button type="submit" color="primary" className="btn button-save me-2">
                        <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={handleResetForm} // <- Updated here
                        className="btn button-cancel" >
                        <i className="fas fa-sync me-1" /> Cancel
                      </Button>

                    </Col> */}
                    <Col className="d-flex mob-center align-items-center justify-content-end mt-2">                
                        {canCreate && selectedDateDisableStatus === 0 && (
                          <Button type="submit" color="primary" className="btn button-save me-2">
                            <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                          </Button>
                        )}
                        <Button type="button" className="btn button-cancel me-2" onClick={handleResetForm}>
                          <i className="fas fa-sync me-1" /> Cancel
                        </Button>
                      </Col>
                    </Row>
                  {/* </Row> */}
                </Form>
              </Col>
              <Col sm={12} xl={4} className="pb-xl-0 pb-4 apporder-xl-0">
                <FormGroup>
                  <StatusCalendar setSelectedDate={setSelectedDate} selectedDate={selectedDate} dateStatusMap={monthDetails} setActiveStartDate={setActiveStartDate} />
                </FormGroup>
              </Col>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="timesheet-table">
              <TableContainer columns={columns} data={timesheetList && timesheetList.length > 0 ? timesheetList:[]} isGlobalFilter customPageSize={10} />
              <div className="mt-3 d-flex gap-3 flex-wrap">
                <p><strong>Total Worked Hours: <span className="text-danger">{totalWorked} Hrs</span></strong></p>
                <p><strong>Total Approved Hours: <span className="text-success">{totalApproved} Hrs</span></strong></p>
              </div>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TimeSheetApply;
